import { Col } from 'antd';
import { displayFieldType } from '@sonar-software/sonar-ts-lib';
import { FieldNodeList } from './';
import { ifClickHandler } from '@lib/events';
import { PeriscopeSfField } from '@sonar-software/sonar-ts-types';
import React, { useMemo } from 'react';
import { SonarRecord, SonarRecordReferences, SonarRecordRow, SonarRecordTags } from '@ui/records';

export type FieldClickHandler = (field: PeriscopeSfField) => void;

export interface PeriscopeFieldProps {
	field: PeriscopeSfField;
	onFieldClick?: FieldClickHandler;
	onParentClick?: FieldClickHandler;
	showParent?: boolean;
	source?: string;
	useApiName?: boolean;
}

export const PeriscopeField: React.FC<PeriscopeFieldProps> = ({
	field,
	onFieldClick,
	onParentClick,
	showParent,
	source,
	useApiName
}) => {
	const fieldType = displayFieldType(field.type ?? field.rawSalesforceType ?? '');
	const name = useApiName ? field.internalName : field.displayName;
	const parentName = useApiName ? field.parentInternalName : field.parentDisplayName;
	const relatedIntegrations = field.linkedIntegrations;

	const handleFieldClick = useMemo(() => ifClickHandler(onFieldClick, field), [ field, onFieldClick ]);
	const handleParentClick = useMemo(() => ifClickHandler(onParentClick, field), [ field, onParentClick ]);

	return (
		<SonarRecord
			name={ name }
			onClick={ handleFieldClick }
			onClickParent={ handleParentClick }
			parentName={ showParent ? parentName : '' }
			relatedOrgs={ relatedIntegrations }
			secondaryInfo={ fieldType }
			sonarObjectId={ field.sonarObjectId }
			source={ source }>
			<SonarRecordRow condition={ Boolean(field.componentTypes?.length) }>
				<Col span={ 24 }>
					<FieldNodeList
						label='Component Types'
						nodes={ field.componentTypes?.map((componentType) => ({ name: componentType })) ?? [] } />
				</Col>
			</SonarRecordRow>
			<SonarRecordRow condition={ Boolean(field.nodes?.length) }>
				<Col span={ 24 }>
					<FieldNodeList
						label='Nodes'
						nodes={ field.nodes ?? [] } />
				</Col>
			</SonarRecordRow>
			<SonarRecordRow>
				<Col
					className='align-bottom'
					flex='none'>
					<SonarRecordReferences
						parentName={ field.parentDisplayName }
						relatedCounts={ field.relatedCounts }
						sonarObjectId={ field.sonarObjectId } />
				</Col>
				<Col flex='auto'>
					<SonarRecordTags sonarObjectId={ field.sonarObjectId } />
				</Col>
			</SonarRecordRow>
		</SonarRecord>
	);
};
